import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { purchaseAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { SearchableSelect } from '@/components/SearchableSelect';
import {
  ClipboardList, Mail, Loader2, Trash2, RefreshCw, Send, FileText, Plus, X, Paperclip, Users, Eye, Play, AlertTriangle, CheckCircle2, Clock
} from 'lucide-react';
import { toast } from 'sonner';
import { showApiError } from '@/lib/errorHandler';
import { EntityLink } from '@/components/EntityLink';

export default function IndentPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialStatus = searchParams.get('status') || '';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState(initialStatus || '');
  const [selectedItems, setSelectedItems] = useState({});
  const [rfqDialog, setRfqDialog] = useState(null);
  const [rfqMessage, setRfqMessage] = useState('');
  const [sendingRfq, setSendingRfq] = useState(false);
  const [removingId, setRemovingId] = useState('');
  
  // RFQ Enhancement state
  const [rfqSuppliers, setRfqSuppliers] = useState([]); // Multiple suppliers for RFQ
  const [rfqItems, setRfqItems] = useState([]); // Items with editable quantities
  const [rfqAttachments, setRfqAttachments] = useState([]); // File attachments
  const [allSuppliers, setAllSuppliers] = useState([]); // All approved suppliers for dropdown
  
  // Assign Supplier Dialog state
  const [assignSupplierDialog, setAssignSupplierDialog] = useState(null);
  const [assigningSupplier, setAssigningSupplier] = useState(false);
  
  // Auto-Indent Preview state
  const [autoIndentPreview, setAutoIndentPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [runningAutoIndent, setRunningAutoIndent] = useState(false);

  useEffect(() => { loadIndent(); loadAllSuppliers(); }, [statusFilter]);

  const loadIndent = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter && statusFilter !== 'all_statuses') params.status = statusFilter;
      const res = await purchaseAPI.listIndent(params);
      setData(res.data);
      setSelectedItems({});
    } catch { toast.error('Failed to load indent'); }
    finally { setLoading(false); }
  };
  
  const loadAllSuppliers = async () => {
    try {
      const res = await purchaseAPI.getSuppliers();
      // API returns array directly, not { suppliers: [...] }
      const suppliers = Array.isArray(res.data) ? res.data : (res.data.suppliers || []);
      setAllSuppliers(suppliers);
    } catch { /* silent */ }
  };

  const toggleItem = (id) => {
    setSelectedItems(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleSupplierItems = (items) => {
    const allSelected = items.every(i => selectedItems[i.id]);
    const updated = { ...selectedItems };
    items.forEach(i => { updated[i.id] = !allSelected; });
    setSelectedItems(updated);
  };

  const removeItem = async (id) => {
    setRemovingId(id);
    try {
      await purchaseAPI.removeFromIndent(id);
      toast.success('Item removed');
      loadIndent();
    } catch (err) { showApiError(err, 'Failed to remove'); }
    finally { setRemovingId(''); }
  };

  const [deletingBulk, setDeletingBulk] = useState(false);
  
  const bulkDeleteItems = async () => {
    const selectedIds = Object.keys(selectedItems).filter(k => selectedItems[k]);
    if (selectedIds.length === 0) {
      toast.error('No items selected');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to delete ${selectedIds.length} item(s) from indent?`)) {
      return;
    }
    
    setDeletingBulk(true);
    try {
      // Delete items one by one (or we could create a bulk API)
      let successCount = 0;
      let failCount = 0;
      for (const id of selectedIds) {
        try {
          await purchaseAPI.removeFromIndent(id);
          successCount++;
        } catch {
          failCount++;
        }
      }
      if (successCount > 0) {
        toast.success(`Deleted ${successCount} item(s) from indent`);
      }
      if (failCount > 0) {
        toast.error(`Failed to delete ${failCount} item(s)`);
      }
      setSelectedItems({});
      loadIndent();
    } catch (err) {
      toast.error('Failed to delete items');
    } finally {
      setDeletingBulk(false);
    }
  };

  const openRfqDialog = (group) => {
    // Get all selected indent items (status = 'indent' or 'pending_quote')
    let selected;
    if (group) {
      selected = group.items.filter(i => selectedItems[i.id] && (i.status === 'indent' || i.status === 'pending_quote'));
    } else {
      // Called from global action bar - get all selected items eligible for RFQ
      selected = data?.suppliers?.flatMap(g => g.items.filter(i => selectedItems[i.id] && (i.status === 'indent' || i.status === 'pending_quote'))) || [];
    }
    
    if (selected.length === 0) {
      toast.error('Select items to send RFQ');
      return;
    }
    
    // Initialize RFQ items with editable quantities
    setRfqItems(selected.map(item => ({
      id: item.id,
      item_id: item.item_id,
      unique_id: item.unique_id,
      item_name: item.item_name,
      required_quantity: item.required_quantity,
      rfq_quantity: item.required_quantity, // Editable, default to required
      unit_of_measure: item.unit_of_measure || 'Nos',
    })));
    
    // Initialize suppliers - collect from items' rfq_suppliers, then group supplier as fallback
    const collectedSuppliers = [];
    const seenIds = new Set();
    selected.forEach(item => {
      (item.rfq_suppliers || []).forEach(s => {
        if (s.supplier_id && !seenIds.has(s.supplier_id)) {
          seenIds.add(s.supplier_id);
          collectedSuppliers.push({ supplier_id: s.supplier_id, supplier_name: s.supplier_name, email: s.email });
        }
      });
    });
    // Fallback to group supplier if nothing collected from items
    if (collectedSuppliers.length === 0 && group?.supplier_id && !seenIds.has(group.supplier_id)) {
      collectedSuppliers.push({ supplier_id: group.supplier_id, supplier_name: group.supplier_name });
    }
    setRfqSuppliers(collectedSuppliers);
    
    setRfqAttachments([]);
    setRfqMessage('');
    setRfqDialog({ open: true });
  };
  
  const addRfqSupplier = (supplierId) => {
    if (!supplierId) return;
    const supplier = allSuppliers.find(s => s.id === supplierId);
    if (!supplier) return;
    if (rfqSuppliers.some(s => s.supplier_id === supplierId)) {
      toast.error('Supplier already added');
      return;
    }
    setRfqSuppliers(prev => [...prev, { supplier_id: supplier.id, supplier_name: supplier.supplier_name, email: supplier.contact_email || supplier.email }]);
  };
  
  const removeRfqSupplier = (supplierId) => {
    setRfqSuppliers(prev => prev.filter(s => s.supplier_id !== supplierId));
  };
  
  const updateRfqItemQty = (itemId, qty) => {
    setRfqItems(prev => prev.map(item => 
      item.id === itemId ? { ...item, rfq_quantity: qty } : item
    ));
  };
  
  const handleRfqFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    setRfqAttachments(prev => [...prev, ...files]);
  };
  
  const removeRfqAttachment = (index) => {
    setRfqAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const sendRfq = async () => {
    if (!rfqDialog) return;
    if (rfqSuppliers.length === 0) {
      toast.error('Add at least one supplier to send RFQ');
      return;
    }
    if (rfqItems.length === 0) {
      toast.error('No items to send RFQ');
      return;
    }
    
    setSendingRfq(true);
    try {
      let res;
      if (rfqAttachments.length > 0) {
        const formData = new FormData();
        formData.append('data', JSON.stringify({
          suppliers: rfqSuppliers.map(s => ({ supplier_id: s.supplier_id, supplier_name: s.supplier_name })),
          items: rfqItems.map(i => ({ indent_id: i.id, item_id: i.item_id, rfq_quantity: parseFloat(i.rfq_quantity) || i.required_quantity })),
          message: rfqMessage,
        }));
        rfqAttachments.forEach((file) => {
          formData.append('attachments', file);
        });
        res = await purchaseAPI.sendRFQWithAttachments(formData);
      } else {
        res = await purchaseAPI.sendRFQ({
          suppliers: rfqSuppliers.map(s => ({ supplier_id: s.supplier_id, supplier_name: s.supplier_name })),
          items: rfqItems.map(i => ({ indent_id: i.id, item_id: i.item_id, rfq_quantity: parseFloat(i.rfq_quantity) || i.required_quantity })),
          message: rfqMessage,
        });
      }
      const data = res.data;
      if (data.email_failures && data.email_failures.length > 0) {
        toast.warning(`RFQ sent to ${data.sent} of ${data.total_suppliers} supplier(s). Failed: ${data.email_failures.join('; ')}`, { duration: 10000 });
      } else {
        toast.success(`RFQ sent to ${data.sent || rfqSuppliers.length} supplier(s)`);
      }
      setRfqDialog(null);
      loadIndent();
    } catch (err) { showApiError(err, 'Failed to send RFQ'); }
    finally { setSendingRfq(false); }
  };
  
  // Assign Supplier to Indent Item
  const openAssignSupplierDialog = (item) => {
    // Pre-populate from rfq_suppliers array (saved previously), else fall back to supplier_id
    let existing = [];
    if (item.rfq_suppliers && item.rfq_suppliers.length > 0) {
      existing = item.rfq_suppliers.map(s => ({ supplier_id: s.supplier_id, supplier_name: s.supplier_name }));
    } else if (item.supplier_id) {
      existing = [{ supplier_id: item.supplier_id, supplier_name: item.supplier_name }];
    }
    setAssignSupplierDialog({ item, selectedSuppliers: existing });
  };
  
  const addSupplierToAssign = (supplierId) => {
    if (!supplierId || !assignSupplierDialog) return;
    const supplier = allSuppliers.find(s => s.id === supplierId);
    if (!supplier) return;
    if (assignSupplierDialog.selectedSuppliers.some(s => s.supplier_id === supplierId)) {
      toast.error('Supplier already added');
      return;
    }
    setAssignSupplierDialog(prev => ({
      ...prev,
      selectedSuppliers: [...prev.selectedSuppliers, { supplier_id: supplier.id, supplier_name: supplier.supplier_name }],
    }));
  };
  
  const removeSupplierFromAssign = (supplierId) => {
    setAssignSupplierDialog(prev => ({
      ...prev,
      selectedSuppliers: prev.selectedSuppliers.filter(s => s.supplier_id !== supplierId),
    }));
  };
  
  const saveAssignedSuppliers = async () => {
    if (!assignSupplierDialog) return;
    const primarySupplier = assignSupplierDialog.selectedSuppliers[0];
    
    setAssigningSupplier(true);
    try {
      await purchaseAPI.updateIndentItem(assignSupplierDialog.item.id, {
        supplier_id: primarySupplier?.supplier_id || null,
        supplier_name: primarySupplier?.supplier_name || null,
        rfq_suppliers: assignSupplierDialog.selectedSuppliers, // Store all suppliers for RFQ
      });
      toast.success('Supplier(s) assigned');
      setAssignSupplierDialog(null);
      loadIndent();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign supplier');
    } finally {
      setAssigningSupplier(false);
    }
  };
  
  // Auto-Indent Preview Functions
  const loadAutoIndentPreview = async () => {
    setLoadingPreview(true);
    try {
      const res = await purchaseAPI.autoIndentPreview();
      setAutoIndentPreview(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load preview');
    } finally {
      setLoadingPreview(false);
    }
  };
  
  const runAutoIndent = async () => {
    setRunningAutoIndent(true);
    try {
      const res = await purchaseAPI.autoIndentRun();
      toast.success(res.data.message + ` (${res.data.items_added} items added)`);
      setAutoIndentPreview(null);
      loadIndent();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to run auto-indent');
    } finally {
      setRunningAutoIndent(false);
    }
  };

  const getStatusBadge = (status) => {
    const map = {
      indent: { label: 'Indent', className: 'bg-cyan-50 text-cyan-700 border-cyan-200' },
      pending_quote: { label: 'Pending Quote', className: 'bg-amber-50 text-amber-700 border-amber-200' },
      purchase_request: { label: 'In PR', className: 'bg-blue-50 text-blue-700 border-blue-200' },
      approved: { label: 'Approved', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
      po_sent: { label: 'PO Sent', className: 'bg-purple-50 text-purple-700 border-purple-200' },
    };
    const s = map[status] || { label: status, className: '' };
    return <Badge variant="outline" className={`text-[10px] ${s.className}`}>{s.label}</Badge>;
  };

  return (
    <div className="space-y-4" data-testid="indent-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-xl font-bold tracking-tight flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-cyan-600" /> Purchase Indent
          </h1>
          <p className="text-xs text-muted-foreground">
            {data?.total || 0} items across {data?.suppliers?.length || 0} suppliers
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <Button size="sm" variant="outline" onClick={loadAutoIndentPreview} disabled={loadingPreview} data-testid="auto-indent-preview-btn">
            {loadingPreview ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Eye className="w-3.5 h-3.5 mr-1" />}
            Auto-Indent Preview
          </Button>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40 h-8 text-xs" data-testid="status-filter">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all_statuses">All Statuses</SelectItem>
              <SelectItem value="indent">Indent</SelectItem>
              <SelectItem value="pending_quote">Pending Quote</SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" onClick={loadIndent} data-testid="refresh-indent-btn">
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {/* Global Action Bar - Shows when items are selected */}
      {Object.keys(selectedItems).filter(k => selectedItems[k]).length > 0 && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="text-sm text-blue-700">
                <strong>{Object.keys(selectedItems).filter(k => selectedItems[k]).length}</strong> item(s) selected
              </div>
              <div className="flex gap-2">
                {/* Send RFQ for selected indent/pending_quote items */}
                {(() => {
                  const allSelected = data?.suppliers?.flatMap(g => g.items.filter(i => selectedItems[i.id] && (i.status === 'indent' || i.status === 'pending_quote'))) || [];
                  if (allSelected.length > 0) {
                    const resendCount = allSelected.filter(i => i.status === 'pending_quote').length;
                    return (
                      <Button size="sm" className="h-7 text-xs" onClick={() => openRfqDialog(null)} data-testid="top-send-rfq-btn">
                        <Mail className="w-3 h-3 mr-1" /> {resendCount > 0 ? 'Send/Resend' : 'Send'} RFQ ({allSelected.length} items)
                      </Button>
                    );
                  }
                  return null;
                })()}
                {/* Create PR for selected pending_quote items */}
                {(() => {
                  const allPendingQuote = data?.suppliers?.flatMap(g => g.items.filter(i => selectedItems[i.id] && i.status === 'pending_quote')) || [];
                  if (allPendingQuote.length > 0) {
                    const ids = allPendingQuote.map(i => i.id).join(',');
                    return (
                      <Button size="sm" className="h-7 text-xs bg-blue-600 hover:bg-blue-700"
                        onClick={() => navigate(`/purchase/requests/new?from=indent&indent_ids=${ids}`)}
                        data-testid="top-create-pr-btn">
                        <FileText className="w-3 h-3 mr-1" /> Create PR ({allPendingQuote.length} items)
                      </Button>
                    );
                  }
                  return null;
                })()}
                {/* Bulk Delete */}
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="h-7 text-xs text-red-600 border-red-200 hover:bg-red-50" 
                  onClick={bulkDeleteItems}
                  disabled={deletingBulk}
                  data-testid="bulk-delete-btn"
                >
                  <Trash2 className="w-3 h-3 mr-1" /> 
                  {deletingBulk ? 'Deleting...' : `Delete (${Object.keys(selectedItems).filter(k => selectedItems[k]).length})`}
                </Button>
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setSelectedItems({})} data-testid="clear-selection-btn">
                  Clear Selection
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-32 text-sm text-muted-foreground">Loading...</div>
      ) : (!data?.suppliers || data.suppliers.length === 0) ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <ClipboardList className="w-10 h-10 text-slate-300 mb-3" />
            <p className="text-sm font-medium text-slate-500">No items in indent</p>
            <p className="text-xs text-muted-foreground mt-1">Add items from Low Stock Report or manually.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {data.suppliers.map((group, gi) => {
            const indentItems = group.items.filter(i => i.status === 'indent');
            const pendingQuoteItems = group.items.filter(i => i.status === 'pending_quote');
            const rfqEligibleItems = group.items.filter(i => i.status === 'indent' || i.status === 'pending_quote');
            const selectedInGroup = group.items.filter(i => selectedItems[i.id]);
            const selectedPendingQuote = selectedInGroup.filter(i => i.status === 'pending_quote');
            const selectedRfqEligible = selectedInGroup.filter(i => i.status === 'indent' || i.status === 'pending_quote');
            const allGroupSelected = group.items.length > 0 && group.items.every(i => selectedItems[i.id]);
            return (
              <Card key={group.supplier_id || `no-sup-${gi}`} data-testid={`indent-supplier-${group.supplier_id || 'none'}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <CardTitle className="text-sm font-semibold">{group.supplier_name}</CardTitle>
                      <p className="text-xs text-muted-foreground">{group.items.length} items</p>
                    </div>
                    <div className="flex gap-1.5">
                      {selectedPendingQuote.length > 0 && (
                        <Button size="sm" className="h-7 text-xs bg-blue-600 hover:bg-blue-700"
                          onClick={() => {
                            const ids = selectedPendingQuote.map(i => i.id).join(',');
                            navigate(`/purchase/requests/new?from=indent&indent_ids=${ids}`);
                          }}
                          data-testid={`create-pr-${group.supplier_id || 'none'}`}>
                          <FileText className="w-3 h-3 mr-1" />
                          Purchase Request ({selectedPendingQuote.length})
                        </Button>
                      )}
                      {rfqEligibleItems.length > 0 && (
                        <Button size="sm" className="h-7 text-xs"
                          onClick={() => openRfqDialog(group)}
                          disabled={selectedRfqEligible.length === 0}
                          data-testid={`send-rfq-${group.supplier_id || 'none'}`}>
                          <Mail className="w-3 h-3 mr-1" />
                          {selectedRfqEligible.some(i => i.status === 'pending_quote') ? 'Send/Resend' : 'Send'} RFQ ({selectedRfqEligible.length})
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b bg-muted/50">
                          <th className="p-2.5 w-8">
                            <Checkbox checked={allGroupSelected} onCheckedChange={() => toggleSupplierItems(group.items)} />
                          </th>
                          <th className="text-left p-2.5 font-medium text-muted-foreground">Item Code</th>
                          <th className="text-left p-2.5 font-medium text-muted-foreground">Item Name</th>
                          <th className="text-left p-2.5 font-medium text-muted-foreground">Type</th>
                          <th className="text-center p-2.5 font-medium text-muted-foreground">Req. Qty</th>
                          <th className="text-center p-2.5 font-medium text-muted-foreground">UOM</th>
                          <th className="text-center p-2.5 font-medium text-muted-foreground">Current</th>
                          <th className="text-left p-2.5 font-medium text-muted-foreground">Source</th>
                          <th className="text-center p-2.5 font-medium text-muted-foreground">Status</th>
                          <th className="text-center p-2.5 font-medium text-muted-foreground">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.items.map(item => (
                          <tr key={item.id} className="border-b hover:bg-muted/30">
                            <td className="p-2.5">
                              <Checkbox checked={!!selectedItems[item.id]} onCheckedChange={() => toggleItem(item.id)} />
                            </td>
                            <td className="p-2.5 font-mono font-medium"><EntityLink type="item" id={item.item_id} className="font-mono font-medium text-xs">{item.unique_id}</EntityLink></td>
                            <td className="p-2.5"><EntityLink type="item" id={item.item_id} className="text-xs">{item.item_name}</EntityLink></td>
                            <td className="p-2.5 text-muted-foreground">{item.item_type || '-'}</td>
                            <td className="p-2.5 text-center font-semibold">{item.required_quantity}</td>
                            <td className="p-2.5 text-center text-muted-foreground text-[10px]">
                              {item.selected_uom || item.unit_of_measure || 'Nos'}
                              {item.secondary_uom && item.selected_uom !== item.unit_of_measure && item.conversion_factor > 0 && (
                                <span className="block text-[9px]">= {(item.required_quantity / item.conversion_factor).toFixed(2)} {item.unit_of_measure}</span>
                              )}
                            </td>
                            <td className="p-2.5 text-center text-muted-foreground">{item.current_stock ?? '-'}</td>
                            <td className="p-2.5">
                              {item.source === 'auto' ? (
                                <Badge variant="outline" className="text-[10px] bg-violet-50 text-violet-700 border-violet-200">Auto</Badge>
                              ) : (
                                <span className="text-muted-foreground">{item.source_user_name || 'Manual'}</span>
                              )}
                            </td>
                            <td className="p-2.5 text-center">{getStatusBadge(item.status)}</td>
                            <td className="p-2.5 text-center">
                              <div className="flex items-center justify-center gap-1">
                                {(item.status === 'indent' || item.status === 'pending_quote') && (
                                  <Button size="sm" variant="ghost" className="h-6 text-[10px] text-blue-600 hover:bg-blue-50"
                                    onClick={() => openAssignSupplierDialog(item)}
                                    data-testid={`assign-supplier-${item.id}`}>
                                    <Users className="w-3 h-3 mr-0.5" /> {(item.rfq_suppliers?.length > 0 || item.supplier_id) ? 'Edit' : 'Assign'}
                                  </Button>
                                )}
                                {['indent', 'pending_quote', 'po_sent', 'pr_created', 'purchase_request'].includes(item.status) && (
                                  <Button size="sm" variant="ghost" className="h-6 text-[10px] text-red-500 hover:bg-red-50"
                                    onClick={() => removeItem(item.id)} disabled={removingId === item.id}
                                    data-testid={`remove-indent-${item.id}`}>
                                    {removingId === item.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* RFQ Dialog - Enhanced with multiple suppliers, editable qty, attachments */}
      <Dialog open={!!rfqDialog} onOpenChange={() => setRfqDialog(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-sm flex items-center gap-2">
              <Mail className="w-4 h-4" /> Send Request for Quotation (RFQ)
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Suppliers Section */}
            <div>
              <Label className="text-xs font-medium">Suppliers to Send RFQ *</Label>
              <div className="mt-2">
                <SearchableSelect
                  options={allSuppliers
                    .filter(s => !rfqSuppliers.some(rs => rs.supplier_id === s.id))
                    .map(s => ({ value: s.id, label: s.supplier_name, sub: s.contact_email || s.email || 'No email' }))}
                  value=""
                  onSelect={addRfqSupplier}
                  placeholder="Search & add supplier..."
                  emptyText="No suppliers found"
                  testId="rfq-add-supplier"
                />
              </div>
              {rfqSuppliers.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {rfqSuppliers.map(s => (
                    <Badge key={s.supplier_id} variant="outline" className="text-xs py-1 px-2 flex items-center gap-1">
                      {s.supplier_name}
                      <button onClick={() => removeRfqSupplier(s.supplier_id)} className="ml-1 hover:text-red-500">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
              {rfqSuppliers.length === 0 && (
                <p className="text-[10px] text-amber-600 mt-1">Add at least one supplier to send RFQ</p>
              )}
            </div>

            {/* Items Section with Editable Quantities */}
            <div>
              <Label className="text-xs font-medium">Items ({rfqItems.length})</Label>
              <div className="mt-2 border rounded max-h-[200px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-muted/50 sticky top-0">
                    <tr>
                      <th className="text-left p-2 font-medium">Item</th>
                      <th className="text-center p-2 font-medium w-24">Required</th>
                      <th className="text-center p-2 font-medium w-28">RFQ Qty *</th>
                      <th className="text-center p-2 font-medium w-16">UOM</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {rfqItems.map(item => (
                      <tr key={item.id}>
                        <td className="p-2">
                          <p className="font-medium">{item.item_name}</p>
                          <p className="text-[10px] text-muted-foreground font-mono">{item.unique_id}</p>
                        </td>
                        <td className="p-2 text-center text-muted-foreground">{item.required_quantity}</td>
                        <td className="p-2">
                          <Input
                            type="number"
                            min="0"
                            step="any"
                            value={item.rfq_quantity}
                            onChange={e => updateRfqItemQty(item.id, e.target.value)}
                            className="h-7 text-xs text-center"
                            data-testid={`rfq-qty-${item.id}`}
                          />
                        </td>
                        <td className="p-2 text-center text-muted-foreground">{item.unit_of_measure}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Attachments Section */}
            <div>
              <Label className="text-xs font-medium">Attachments (Optional)</Label>
              <div className="mt-2">
                <input
                  type="file"
                  multiple
                  onChange={handleRfqFileChange}
                  className="hidden"
                  id="rfq-attachments"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => document.getElementById('rfq-attachments').click()}
                >
                  <Paperclip className="w-3 h-3 mr-1" /> Add Files
                </Button>
                {rfqAttachments.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {rfqAttachments.map((file, index) => (
                      <div key={index} className="flex items-center justify-between p-1.5 bg-muted/50 rounded text-xs">
                        <span className="truncate max-w-[250px]">{file.name}</span>
                        <button onClick={() => removeRfqAttachment(index)} className="text-red-500 hover:text-red-700 ml-2">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Message Section */}
            <div>
              <Label className="text-xs font-medium">Additional Message (Optional)</Label>
              <Textarea
                value={rfqMessage}
                onChange={e => setRfqMessage(e.target.value)}
                placeholder="Any specific requirements for the quotation..."
                className="mt-1 text-xs"
                rows={3}
                data-testid="rfq-message"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setRfqDialog(null)}>Cancel</Button>
            <Button 
              size="sm" 
              onClick={sendRfq} 
              disabled={sendingRfq || rfqSuppliers.length === 0} 
              data-testid="confirm-send-rfq"
            >
              {sendingRfq ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}
              Send RFQ to {rfqSuppliers.length} Supplier(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Supplier Dialog */}
      <Dialog open={!!assignSupplierDialog} onOpenChange={() => setAssignSupplierDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-sm flex items-center gap-2">
              <Users className="w-4 h-4" /> Assign Suppliers
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="p-2 bg-muted/50 rounded text-xs">
              <p className="font-medium">{assignSupplierDialog?.item?.item_name}</p>
              <p className="text-muted-foreground font-mono">{assignSupplierDialog?.item?.unique_id}</p>
            </div>
            
            <div>
              <Label className="text-xs font-medium">Add Supplier(s) for RFQ</Label>
              <div className="mt-1">
                <SearchableSelect
                  options={allSuppliers
                    .filter(s => !assignSupplierDialog?.selectedSuppliers?.some(ss => ss.supplier_id === s.id))
                    .map(s => ({ value: s.id, label: s.supplier_name, sub: s.contact_email || s.email || 'No email' }))}
                  value=""
                  onSelect={addSupplierToAssign}
                  placeholder="Search supplier..."
                  emptyText="No suppliers found"
                  testId="assign-supplier-select"
                />
              </div>
            </div>

            {assignSupplierDialog?.selectedSuppliers?.length > 0 && (
              <div className="space-y-1">
                <Label className="text-xs font-medium">Selected Suppliers:</Label>
                {assignSupplierDialog.selectedSuppliers.map((s, i) => (
                  <div key={s.supplier_id} className="flex items-center justify-between p-2 bg-muted/50 rounded text-xs">
                    <span>
                      {i === 0 && <Badge variant="outline" className="text-[9px] mr-1 bg-blue-50">Primary</Badge>}
                      {s.supplier_name}
                    </span>
                    <button onClick={() => removeSupplierFromAssign(s.supplier_id)} className="text-red-500 hover:text-red-700">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setAssignSupplierDialog(null)}>Cancel</Button>
            <Button size="sm" onClick={saveAssignedSuppliers} disabled={assigningSupplier}>
              {assigningSupplier ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Auto-Indent Preview Dialog */}
      <Dialog open={!!autoIndentPreview} onOpenChange={(open) => !open && setAutoIndentPreview(null)}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="auto-indent-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-cyan-600" />
              Auto-Indent Preview
            </DialogTitle>
          </DialogHeader>
          
          {autoIndentPreview && (
            <div className="space-y-4">
              {/* Summary Cards */}
              <div className="grid grid-cols-4 gap-3">
                <Card className="bg-slate-50">
                  <CardContent className="p-3 text-center">
                    <div className="text-xl font-bold text-slate-700">{autoIndentPreview.summary?.total_low_stock || 0}</div>
                    <div className="text-[10px] text-muted-foreground">Low Stock Items</div>
                  </CardContent>
                </Card>
                <Card className="bg-emerald-50 border-emerald-200">
                  <CardContent className="p-3 text-center">
                    <div className="text-xl font-bold text-emerald-700">{autoIndentPreview.summary?.will_add_count || 0}</div>
                    <div className="text-[10px] text-emerald-600">Will Be Added</div>
                  </CardContent>
                </Card>
                <Card className="bg-amber-50 border-amber-200">
                  <CardContent className="p-3 text-center">
                    <div className="text-xl font-bold text-amber-700">{autoIndentPreview.summary?.skipped_existing_indent_count || 0}</div>
                    <div className="text-[10px] text-amber-600">Already In Indent</div>
                  </CardContent>
                </Card>
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="p-3 text-center">
                    <div className="text-xl font-bold text-blue-700">{autoIndentPreview.summary?.skipped_future_pr_count || 0}</div>
                    <div className="text-[10px] text-blue-600">Has Open PR</div>
                  </CardContent>
                </Card>
              </div>
              
              {/* Items Will Be Added */}
              {autoIndentPreview.will_add?.length > 0 && (
                <Card className="border-emerald-200">
                  <CardHeader className="py-2 px-3 bg-emerald-50/50">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                      Items to be Added to Indent ({autoIndentPreview.will_add.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="max-h-48 overflow-y-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-50 sticky top-0">
                          <tr>
                            <th className="text-left px-3 py-2 font-medium">Item</th>
                            <th className="text-left px-3 py-2 font-medium">Type</th>
                            <th className="text-right px-3 py-2 font-medium">Stock</th>
                            <th className="text-right px-3 py-2 font-medium">Min Level</th>
                            <th className="text-right px-3 py-2 font-medium">Shortage</th>
                            <th className="text-left px-3 py-2 font-medium">Supplier</th>
                          </tr>
                        </thead>
                        <tbody>
                          {autoIndentPreview.will_add.map((item, idx) => (
                            <tr key={item.item_id} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                              <td className="px-3 py-2">
                                <div className="font-medium">{item.item_name}</div>
                                <div className="text-[10px] text-muted-foreground">{item.unique_id}</div>
                              </td>
                              <td className="px-3 py-2">{item.item_type}</td>
                              <td className="px-3 py-2 text-right">{item.current_stock}</td>
                              <td className="px-3 py-2 text-right">{item.min_stock_level}</td>
                              <td className="px-3 py-2 text-right font-medium text-red-600">{item.shortage}</td>
                              <td className="px-3 py-2">{item.preferred_supplier_name || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Skipped - Already in Indent */}
              {autoIndentPreview.skipped_existing_indent?.length > 0 && (
                <Card className="border-amber-200">
                  <CardHeader className="py-2 px-3 bg-amber-50/50">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Clock className="w-4 h-4 text-amber-600" />
                      Skipped - Already in Indent ({autoIndentPreview.skipped_existing_indent.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="max-h-36 overflow-y-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-50 sticky top-0">
                          <tr>
                            <th className="text-left px-3 py-2 font-medium">Item</th>
                            <th className="text-right px-3 py-2 font-medium">Shortage</th>
                            <th className="text-left px-3 py-2 font-medium">Reason</th>
                          </tr>
                        </thead>
                        <tbody>
                          {autoIndentPreview.skipped_existing_indent.map((item, idx) => (
                            <tr key={item.item_id} className={idx % 2 === 0 ? 'bg-white' : 'bg-amber-50/30'}>
                              <td className="px-3 py-2">
                                <div className="font-medium">{item.item_name}</div>
                                <div className="text-[10px] text-muted-foreground">{item.unique_id}</div>
                              </td>
                              <td className="px-3 py-2 text-right">{item.shortage}</td>
                              <td className="px-3 py-2">
                                <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">
                                  {item.skip_reason}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Skipped - Open PR with Future Delivery */}
              {autoIndentPreview.skipped_future_pr?.length > 0 && (
                <Card className="border-blue-200">
                  <CardHeader className="py-2 px-3 bg-blue-50/50">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-blue-600" />
                      Skipped - Open PR with Future Delivery ({autoIndentPreview.skipped_future_pr.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="max-h-36 overflow-y-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-50 sticky top-0">
                          <tr>
                            <th className="text-left px-3 py-2 font-medium">Item</th>
                            <th className="text-right px-3 py-2 font-medium">Shortage</th>
                            <th className="text-left px-3 py-2 font-medium">Open PR</th>
                            <th className="text-left px-3 py-2 font-medium">Delivery Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {autoIndentPreview.skipped_future_pr.map((item, idx) => (
                            <tr key={item.item_id} className={idx % 2 === 0 ? 'bg-white' : 'bg-blue-50/30'}>
                              <td className="px-3 py-2">
                                <div className="font-medium">{item.item_name}</div>
                                <div className="text-[10px] text-muted-foreground">{item.unique_id}</div>
                              </td>
                              <td className="px-3 py-2 text-right">{item.shortage}</td>
                              <td className="px-3 py-2">
                                <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700 border-blue-200">
                                  {item.pr_details?.pr_number} ({item.pr_details?.status})
                                </Badge>
                              </td>
                              <td className="px-3 py-2 font-medium text-blue-700">{item.pr_details?.delivery_date}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Empty state */}
              {autoIndentPreview.summary?.total_low_stock === 0 && (
                <Card>
                  <CardContent className="py-8 text-center">
                    <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
                    <p className="text-sm font-medium text-slate-600">All items are above minimum stock levels</p>
                    <p className="text-xs text-muted-foreground">No auto-indent required at this time.</p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
          
          <DialogFooter className="flex justify-between items-center">
            <p className="text-xs text-muted-foreground">
              {autoIndentPreview?.summary?.will_add_count > 0 
                ? `${autoIndentPreview.summary.will_add_count} item(s) will be added to indent`
                : 'No items to add'}
            </p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setAutoIndentPreview(null)}>Close</Button>
              {autoIndentPreview?.summary?.will_add_count > 0 && (
                <Button 
                  size="sm" 
                  onClick={runAutoIndent} 
                  disabled={runningAutoIndent}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="run-auto-indent-btn"
                >
                  {runningAutoIndent ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Play className="w-3.5 h-3.5 mr-1" />}
                  Run Auto-Indent
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
